'use client';

import { useEffect, useRef } from 'react';

import { AccessGuard } from '@ps/features/access-control/components/access-guard/access-guard';
import type { AccessGuardTrigger } from '@ps/features/access-control/components/access-guard/guard-descriptor';
import { LeadFormGuardProvider } from '@ps/features/access-control/components/access-guard/lead-form-guard-context';
import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import { popstoreUrl } from '@ps/functions/utils/urls';
import { LeadFormTriggerActionType } from '@ps/models/lib/lead-form';
import type { realestatelst } from '@ps/models/lib/realestate-listing-page';
import type { CreatorInfo } from '@ps/types/creator';
import { withClientAuth } from '@ps/ui/contexts/auth/with-client-auth';

interface ResourceAccessGateProps {
  bioContext: BioContext;
  slug: string;
  listingId: string;
  resource: realestatelst.ResourceItem;
  sellerProfile: CreatorInfo;
}

export const ResourceAccessGate = withClientAuth(ResourceAccessGateInner);

function ResourceAccessGateInner({
  bioContext,
  slug,
  resource,
  sellerProfile,
}: ResourceAccessGateProps) {
  const storeId = bioContext.creatorId;
  const triggers = buildTriggers(resource);

  function redirectToResource() {
    const url = resolveResourceUrl(storeId, resource);
    window.location.replace(url);
  }

  function handleCancel() {
    window.close();
    // Fallback for tabs that weren't opened programmatically (direct-link,
    // deep-link): send the user back to the realestate listing page.
    window.location.replace(bioUrl(bioContext).realEstateListing(slug));
  }

  return (
    <LeadFormGuardProvider storeId={storeId}>
      <div className="dark">
        <div className="bg-login-gradient flex min-h-screen items-center justify-center px-4 font-sans">
          <AccessGuard
            storeId={storeId}
            guard={{ kind: 'skip-follow', triggers, nonDismissible: true }}
            sellerProfile={sellerProfile}
            onAccessGranted={redirectToResource}
            onCancel={handleCancel}
          >
            {(canProceed, requestAccess, isReady) => (
              <AutoRequestAccess
                canProceed={canProceed}
                isReady={isReady}
                requestAccess={requestAccess}
                onProceed={redirectToResource}
              />
            )}
          </AccessGuard>
        </div>
      </div>
    </LeadFormGuardProvider>
  );
}

function AutoRequestAccess({
  canProceed,
  isReady,
  requestAccess,
  onProceed,
}: {
  canProceed: boolean;
  isReady: boolean;
  requestAccess: () => void;
  onProceed: () => void;
}) {
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    if (!isReady || hasTriggeredRef.current) return;
    hasTriggeredRef.current = true;
    if (canProceed) {
      onProceed();
    } else {
      requestAccess();
    }
  }, [isReady, canProceed, requestAccess, onProceed]);

  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <div className="size-6 animate-spin rounded-full border-2 border-white/20 border-t-white/80" />
      <p className="text-sm text-white/70">Loading resource...</p>
    </div>
  );
}

function buildTriggers(
  resource: realestatelst.ResourceItem,
): AccessGuardTrigger[] {
  switch (resource.type) {
    case 'digital-product':
      return [
        {
          type: LeadFormTriggerActionType.DigitalProduct,
          id: resource.listingId,
        },
      ];
  }
}

function resolveResourceUrl(
  storeId: string,
  resource: realestatelst.ResourceItem,
): string {
  switch (resource.type) {
    case 'digital-product':
      return popstoreUrl('/products', storeId, resource.listingId).toString();
  }
}
